import { useMount, useUnmount } from 'ahooks';
import React from 'react';
import { Box, Spinner } from '@apia/theme';
import useTemplate from '../../../../../templater/useTemplate';
import store from '../../../../../store';
import { remoteModalsActions } from '../../../../../store/remoteModalsSlice';
import { Confirm } from '../../../../common';
import {
  TUploadDocumentModalData,
  documentUploadModalId,
  TUploadModalStore,
} from '.';
import Form, { TOnValidate } from '../../../../common/validation/Form';
import { hasSucceedFormValidation } from '../../../../../store/validation/util';
import useFieldValue from '../../../../common/validation/hooks/useFieldValue';
import validationsStore from '../../../../../store/validation/validationStore';
import makeSingleImperativeComponent from '../../../../../classes/imperative/makeSingleImperativeComponent';

export const UploadModalContext = React.createContext<TUploadDocumentModalData>(
  {} as TUploadDocumentModalData,
);

const [, uploadModalMethods, ModalRenderer] = makeSingleImperativeComponent<
  object,
  {
    updateState: Partial<TUploadModalStore>;
  }
>()({
  Component: ({ data }: { data: TUploadDocumentModalData }) => {
    const isReadonly =
      useFieldValue<boolean>({
        formName: data.api.modalStoreId,
        name: 'isReadonly',
      }) ?? false;

    const template = useTemplate('modals.documentUpload', {
      props: React.useMemo(
        () => ({ ...data.api.configurationObject, isReadonly }),
        [data.api.configurationObject, isReadonly],
      ),
      reparseOnPropsChange: true,
    });
    const contextValue = React.useMemo(() => data, [data]);

    const handleCancel = React.useCallback(() => {
      store.dispatch(remoteModalsActions.close(documentUploadModalId));
    }, []);

    useMount(() => {
      if (data.api.getValidationStore().versioningFile) return;
      void data.api.ajaxUploadStart(data.newFiles);
      validationsStore.registerField(data.api.modalStoreId, {
        name: 'metadata',
      });
    });

    useUnmount(() => {
      data.api.onCloseModal();
    });

    const handleValidate = React.useCallback<
      TOnValidate<TUploadModalStore, TUploadModalStore>
    >(
      (result) => {
        if (hasSucceedFormValidation(result)) {
          void data.api.confirmDropModal({
            langId: data.langId,
            translatingFile: data.translatingFile,
          });
        }
      },
      [data.api, data.langId, data.translatingFile],
    );

    if (!template) return <Spinner tabIndex={0} />;

    return (
      <Form<TUploadModalStore>
        initialValues={data.initialValues}
        name={data.api.modalStoreId}
        avoidFieldsTouch
        onValidate={handleValidate}
        avoidFieldsOverride
        unregisterOnUnmount={false}
      >
        <Box variant="layout.common.modals.documentUpload">
          <Confirm onCancel={handleCancel} className="documentUploadModal">
            <UploadModalContext.Provider value={contextValue}>
              {template}
            </UploadModalContext.Provider>
          </Confirm>
        </Box>
      </Form>
    );
  },
});

export { uploadModalMethods };
export default ModalRenderer;
